import React from 'react';
import { FormattedTextarea } from '../FormattedTextarea';
import { ToggleSwitch } from '../../../sdk';
import { uploadVideoToS3 } from '../../../utils/storageApi';
import { getAuthHeaders } from '../../../utils/authHeaders';

const DEFAULT_LIMITS = {
  maxFileSize: 100 * 1024 * 1024, warnFileSize: 50 * 1024 * 1024,
  maxDuration: 300, maxResolution: 3840,
};

const GUIDE_STYLES = {
  '1:1':  { top: '9%',    left: '6%',    width: '88%',   height: '80%' },
  '4:5':  { top: '9%',    left: '15%',   width: '70%',   height: '80%' },
  '16:9': { top: '24%',   left: '5%',    width: '90%',   height: '52%' },
};

const RESOLUTION_MAP = {
  '1:1':  '1080\u00d71080',
  '4:5':  '1080\u00d71350',
  '16:9': '1920\u00d71080',
};

const BTN_COL_STYLE = { width: 56, flexShrink: 0 };

export function VideoWidgetEditor({ videoWidget, onChange, allSlides = [], resolveErrors = {} }) {
  const hasWidget = !!videoWidget;
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [uploadWarnings, setUploadWarnings] = React.useState([]);
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const resolveError = videoWidget?.asset_id ? resolveErrors[videoWidget.asset_id] : null;
  const previousDataRef = React.useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleToggle = (enabled) => {
    if (enabled) {
      onChange(previousDataRef.current || {
        label: 'Video', caption: 'Watch our story unfold.',
        aspectRatio: '16:9', size: 'large',
      });
    } else {
      previousDataRef.current = videoWidget;
      onChange(null);
    }
  };

  const [limits, setLimits] = React.useState(DEFAULT_LIMITS);
  const [featureFlags, setFeatureFlags] = React.useState({ legacy_upload: false, s3_configured: false });

  React.useEffect(() => {
    let cancelled = false;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${backendUrl}/api/config/upload-limits`);
    xhr.onload = () => {
      if (cancelled) return;
      try {
        const d = JSON.parse(xhr.responseText);
        if (d?.video) setLimits({
          maxFileSize: d.video.max_file_size ?? DEFAULT_LIMITS.maxFileSize,
          warnFileSize: d.video.warn_file_size ?? DEFAULT_LIMITS.warnFileSize,
          maxDuration: d.video.max_duration ?? DEFAULT_LIMITS.maxDuration,
          maxResolution: d.video.max_resolution ?? DEFAULT_LIMITS.maxResolution,
        });
      } catch { /* defaults */ }
    };
    xhr.send();
    const xhr2 = new XMLHttpRequest();
    xhr2.open('GET', `${backendUrl}/api/config/flags`);
    xhr2.onload = () => { if (!cancelled) try { setFeatureFlags(JSON.parse(xhr2.responseText)); } catch {} };
    xhr2.send();
    return () => { cancelled = true; };
  }, [backendUrl]);

  const validateClientSide = (file) => new Promise((resolve) => {
    const errors = [], warnings = [], info = {};
    const { maxFileSize, warnFileSize, maxDuration, maxResolution } = limits;
    if (!file.type.startsWith('video/')) { resolve({ valid: false, errors: ['Not a video file.'], warnings: [], info: { size_bytes: file.size } }); return; }
    if (file.size > maxFileSize) { resolve({ valid: false, errors: [`Too large (${(file.size/1048576).toFixed(0)} MB). Max ${(maxFileSize/1048576).toFixed(0)} MB.`], warnings: [], info: { size_bytes: file.size } }); return; }
    if (file.size > warnFileSize) warnings.push(`Large file (${(file.size/1048576).toFixed(0)} MB).`);

    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);
    let done = false;
    const cleanup = () => { if (!done) { done = true; URL.revokeObjectURL(url); video.removeAttribute('src'); video.load(); } };
    const t = setTimeout(() => { cleanup(); resolve({ valid: true, errors: [], warnings, info: { size_bytes: file.size } }); }, 5000);

    video.onloadedmetadata = () => {
      clearTimeout(t);
      const dur = video.duration, w = video.videoWidth, h = video.videoHeight;
      info.duration = Math.round(dur * 100) / 100;
      info.duration_formatted = `${dur/60|0}:${(Math.floor(dur)%60).toString().padStart(2,'0')}`;
      info.width = w; info.height = h; info.size_bytes = file.size;
      if (dur > maxDuration) errors.push(`Too long (${info.duration_formatted}). Max ${Math.floor(maxDuration/60)} min.`);
      if (w > maxResolution || h > maxResolution) errors.push(`Resolution too high (${w}×${h}).`);
      cleanup();
      resolve({ valid: errors.length === 0, errors, warnings, info });
    };
    video.onerror = () => { clearTimeout(t); cleanup(); resolve({ valid: true, errors: [], warnings, info: { size_bytes: file.size } }); };
    video.src = url;
  });

  const tryS3Upload = async (file, cw) => {
    // Retry once on transient failures (network glitch, S3 cold start)
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        if (attempt > 0) {
          setUploadProgress({ phase: 'uploading', percent: 0, message: 'Retrying upload...' });
          await new Promise(r => setTimeout(r, 2000)); // 2s delay before retry
        }
        const r = await uploadVideoToS3(file, (p) => setUploadProgress(p));
        // Surface quota warning from server (upload still succeeded)
        const allWarnings = [...cw];
        if (r.quota_warning) allWarnings.push(r.quota_warning);
        if (allWarnings.length) setUploadWarnings(allWarnings);
        // Persist stable internal URL — NOT the temporary presigned URL.
        // /api/media/asset/{id} redirects (302) to a fresh presigned URL.
        return { asset_id: r.asset_id, src: `/api/media/asset/${r.asset_id}`, duration: r.asset?.duration_seconds || null };
      } catch (err) {
        lastError = err;
        console.error(`[VideoUpload] S3 upload attempt ${attempt + 1} failed:`, err.message || err);
        // Don't retry on client errors (4xx) — only on network/server errors
        // Check httpStatus property (set by storageApi) OR status code in message
        const status = err.httpStatus;
        const msg = err.message || '';
        if (status >= 400 && status < 500) break;
        if (msg.includes('(4') || msg.includes('not allowed') || msg.includes('quota') || msg.includes('413')) break;
      }
    }
    throw lastError;
  };

  const tryLegacyUpload = async (file, cw) => {
    // Legacy disk upload is disabled server-side (410 Gone).
    // This fallback surfaces a clear error if S3 upload failed and legacy was attempted.
    const fd = new FormData(); fd.append('file', file);
    const authHeaders = await getAuthHeaders();
    const r = await new Promise((res, rej) => {
      const x = new XMLHttpRequest();
      x.open('POST', `${backendUrl}/api/widgets/upload-video`);
      if (authHeaders['Authorization']) x.setRequestHeader('Authorization', authHeaders['Authorization']);
      x.upload.onprogress = (e) => { if (e.lengthComputable) setUploadProgress({ phase: 'uploading', percent: Math.round(e.loaded/e.total*100), message: `Uploading... ${Math.round(e.loaded/e.total*100)}%` }); };
      x.onload = () => { let d; try { d = JSON.parse(x.responseText); } catch {} res({ status: x.status, ok: x.status>=200&&x.status<300, data: d, text: x.responseText }); };
      x.onerror = () => rej(new Error('Network error'));
      x.ontimeout = () => rej(new Error('Timeout'));
      x.timeout = 120000; x.send(fd);
    });
    if (!r.ok) {
      // Handle 410 Gone — legacy upload is permanently disabled
      if (r.status === 410) {
        throw Object.assign(new Error('Upload unavailable'), {
          uploadError: {
            message: 'Video upload failed',
            errors: ['S3 upload failed and the fallback server upload is no longer available. Please check your connection and try again.'],
            info: {}
          }
        });
      }
      const dt = r.data?.detail;
      if (dt && typeof dt === 'object' && Array.isArray(dt.errors)) throw Object.assign(new Error(dt.message||'Incompatible'), { uploadError: { message: dt.message, errors: dt.errors, info: dt.info||{} } });
      if (typeof dt === 'string') throw Object.assign(new Error(dt), { uploadError: { message: dt, errors: [dt], info: {} } });
      throw Object.assign(new Error('Upload failed'), { uploadError: { message: 'Failed', errors: [r.text||'Upload failed.'], info: {} } });
    }
    const aw = [...new Set([...cw, ...(r.data?.validation?.warnings||[])])];
    if (aw.length) setUploadWarnings(aw);
    return { src: `${backendUrl}${r.data.url}`, duration: r.data.duration, validationInfo: r.data?.validation?.info||null };
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadError(null); setUploadWarnings([]); setUploadProgress(null); setUploading(true);
    try {
      const cv = await validateClientSide(file);
      if (!cv.valid) { setUploadError({ message: 'Video not compatible', errors: cv.errors, info: cv.info }); setUploading(false); return; }
      const cw = cv.warnings||[], cd = cv.info?.duration||null;
      let ur;
      try { ur = await tryS3Upload(file, cw); if (!ur.duration && cd) ur.duration = cd; }
      catch (e2) {
        console.error('[VideoUpload] S3 upload failed:', e2.message || e2);
        if (featureFlags.legacy_upload) { setUploadProgress({ phase:'uploading', percent:0, message:'Retrying via server...' }); ur = await tryLegacyUpload(file, cw); }
        else {
          const detail = e2.message || 'Unknown error';
          throw Object.assign(new Error('Upload failed'), {
            uploadError: {
              message: 'Upload failed',
              errors: [detail.includes('network') || detail.includes('Network')
                ? 'Network error — check your connection and try again.'
                : detail.includes('timed out') || detail.includes('Timeout')
                ? 'Upload timed out — the file may be too large for your connection.'
                : `Storage error: ${detail}`],
              info: {}
            }
          });
        }
      }
      onChange({ ...videoWidget, src: ur.src, duration: ur.duration, asset_id: ur.asset_id||null, validationInfo: ur.validationInfo||null });
    } catch (err) {
      if (err.uploadError) setUploadError(err.uploadError);
      else setUploadError({ message: 'Upload failed', errors: [err.message||'Network error.'], info: {} });
    } finally { setUploading(false); setUploadProgress(null); if (fileInputRef.current) fileInputRef.current.value=''; }
  };

  const handleRemoveVideo = (e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Remove this video?')) return;
    setUploadError(null); setUploadWarnings([]); setUploadProgress(null);
    onChange({ ...videoWidget, src: '', duration: null, asset_id: null, validationInfo: null });
  };

  const openFilePicker = () => {
    if (!uploading) { setUploadError(null); setUploadWarnings([]); setUploadProgress(null); fileInputRef.current?.click(); }
  };

  const formatDuration = (s) => { if (!s) return ''; return `${Math.floor(s/60)}:${Math.round(s%60).toString().padStart(2,'0')}`; };

  const aspectRatios = [{ value:'1:1',label:'1:1' },{ value:'4:5',label:'4:5' },{ value:'16:9',label:'16:9' }];
  const isLandscape = videoWidget?.aspectRatio === '16:9';
  const sizeOptions = isLandscape
    ? [{ value:'large',label:'100%' },{ value:'small',label:'75%' }]
    : [{ value:'large',label:'50%' },{ value:'small',label:'30%' }];

  const currentRatio = videoWidget?.aspectRatio || '16:9';
  const guideStyle = GUIDE_STYLES[currentRatio] || GUIDE_STYLES['16:9'];
  const resolution = RESOLUTION_MAP[currentRatio] || '';
  const hasFile = videoWidget?.src && !resolveError;

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="module-header">Video Widget</div>
          <div className="module-desc">Media card with video.</div>
        </div>
        <ToggleSwitch checked={hasWidget} onChange={handleToggle} size="sm" />
      </div>

      {hasWidget && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-[11px] text-white/55 mb-1">Label</div>
            <input value={videoWidget.label||''} onChange={(e) => onChange({ ...videoWidget, label: e.target.value })} className="input-base" placeholder="e.g. Behind the Scenes" />
          </div>

          <div>
            <div className="text-[11px] text-white/55 mb-1">Caption</div>
            <FormattedTextarea value={videoWidget.caption||''} onValueChange={(val) => onChange({ ...videoWidget, caption: val })} className="textarea-base" rows={2} maxLength={300} placeholder="Description text" />
          </div>

          {/* ── [SCREEN] [UPLOAD] [BUTTONS] row ── */}
          <div className="flex items-stretch gap-2">
            {/* LEFT: Cinema monitor (display only) */}
            <div className="media-cinema">
              <div className="media-cinema-inner">
                <span className="media-cinema-label" style={{ top: 6, left: 8 }}>{currentRatio}</span>
                <span className="media-cinema-label" style={{ top: 6, right: 8 }}>{resolution}</span>
                <div className="media-cinema-guide" style={guideStyle} />
                {!hasFile && !uploading && (
                  <div className="media-cinema-crosshair" style={{
                    top: `calc(${guideStyle.top} + ${guideStyle.height} / 2)`,
                    left: `calc(${guideStyle.left} + ${guideStyle.width} / 2)`,
                  }}>+</div>
                )}
                {hasFile && (
                  <>
                    <div className="media-cinema-media" style={guideStyle}>
                      <video src={videoWidget.src} muted playsInline />
                    </div>
                    {videoWidget.duration && (
                      <div className="media-cinema-duration">{formatDuration(videoWidget.duration)}</div>
                    )}
                    <div className="media-cinema-delete" onClick={handleRemoveVideo} title="Remove video">
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12" /></svg>
                    </div>
                  </>
                )}
                {uploading && uploadProgress?.percent > 0 && (
                  <div style={{ position: 'absolute', bottom: '10%', left: '15%', right: '15%', height: 2, borderRadius: 1, background: 'rgba(255,255,255,0.06)', zIndex: 4 }}>
                    <div style={{ width: `${uploadProgress.percent}%`, height: '100%', borderRadius: 1, background: 'rgba(52,211,153,0.6)', transition: 'width 0.3s ease' }} />
                  </div>
                )}
              </div>
            </div>

            {/* CENTER: Standard upload dropzone */}
            <div
              className={`upload-dropzone upload-dropzone-inline${uploading ? ' upload-dropzone-loading' : ''}`}
              onClick={openFilePicker}
            >
              <div className="upload-dropzone-content">
                {uploading ? (
                  <div className="upload-dropzone-text">{uploadProgress?.message || 'Uploading...'}</div>
                ) : (
                  <>
                    <svg className="upload-dropzone-icon" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <div className="upload-dropzone-text">{hasFile ? 'Replace' : 'Upload'}</div>
                    <div className="upload-dropzone-sub">MP4 / MOV / WebM</div>
                  </>
                )}
              </div>
            </div>

            {/* RIGHT: Buttons — fixed width */}
            <div className="flex flex-col gap-1 justify-center" style={BTN_COL_STYLE}>
              <span className="opt-group-label text-center">Ratio</span>
              {aspectRatios.map((r) => (
                <button key={r.value}
                  onClick={() => onChange({ ...videoWidget, aspectRatio: r.value, size: 'large', ...(r.value !== '16:9' ? { expandVertical: false } : {}) })}
                  className={`opt-cell${currentRatio === r.value ? ' opt-cell-active' : ''}`}
                  style={{ justifyContent: 'center', padding: '0 6px' }}
                >
                  <div className={`opt-cell-led${currentRatio === r.value ? ' opt-cell-led-active' : ''}`} />
                  {r.label}
                </button>
              ))}
              <div style={{ height: 3 }} />
              <span className="opt-group-label text-center">Size</span>
              {sizeOptions.map((s) => (
                <button key={s.value}
                  onClick={() => onChange({ ...videoWidget, size: s.value })}
                  className={`opt-cell${(videoWidget.size||'large')===s.value ? ' opt-cell-active' : ''}`}
                  style={{ justifyContent: 'center', padding: '0 6px' }}
                >
                  <div className={`opt-cell-led${(videoWidget.size||'large')===s.value ? ' opt-cell-led-active' : ''}`} />
                  {s.label}
                </button>
              ))}
              <div style={{ height: 22, marginTop: 2 }}>
                {isLandscape && (
                  <label className="flex items-center gap-1 cursor-pointer select-none" title="Expand vertically">
                    <ToggleSwitch checked={!!videoWidget.expandVertical} onChange={(v) => onChange({ ...videoWidget, expandVertical: v })} size="sm" />
                    <span style={{ fontSize: 7, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.3px' }}>Exp</span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Status LEDs below the row */}
          <div className="media-status-row">
            <div className="media-status-item" title={hasFile ? 'File loaded' : 'No file'}>
              <svg className="media-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <div className={`media-status-led${hasFile ? ' media-status-led-on-green' : ''}`} />
            </div>
            <div className="media-status-item" title={videoWidget.asset_id ? 'Cloud stored' : 'Not in cloud'}>
              <svg className="media-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
              </svg>
              <div className={`media-status-led${videoWidget.asset_id ? ' media-status-led-on-blue' : ''}`} />
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />

          {/* ── Error/warning/resolve banners ── */}
          {uploadError && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-red-400">{uploadError.message || 'Video not compatible'}</div>
                  {uploadError.errors.map((e, i) => <div key={i} className="mt-1 text-[11px] text-red-300/80 leading-relaxed">{e}</div>)}
                </div>
                <button onClick={() => setUploadError(null)} className="text-red-400/60 hover:text-red-400 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          )}

          {uploadWarnings.length > 0 && !uploadError && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-amber-400">Heads up</div>
                  {uploadWarnings.map((w, i) => <div key={i} className="mt-1 text-[11px] text-amber-300/80 leading-relaxed">{w}</div>)}
                </div>
                <button onClick={() => setUploadWarnings([])} className="text-amber-400/60 hover:text-amber-400 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          )}

          {resolveError === 'not_found' && (
            <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-3">
              <div className="text-xs font-semibold text-orange-400">Video not found</div>
              <div className="mt-1 text-[11px] text-orange-300/80">Original missing. Please upload a replacement.</div>
              <button onClick={() => handleRemoveVideo(null)} className="mt-2 text-xs text-orange-400/80 hover:text-orange-300 underline underline-offset-2">Clear &amp; upload new video</button>
            </div>
          )}

          {resolveError === 'deleted' && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3">
              <div className="text-xs font-semibold text-red-400">Video deleted</div>
              <div className="mt-1 text-[11px] text-red-300/80">Deleted from storage. Please upload a replacement.</div>
              <button onClick={() => handleRemoveVideo(null)} className="mt-2 text-xs text-red-400/80 hover:text-red-300 underline underline-offset-2">Clear &amp; upload new video</button>
            </div>
          )}

          {resolveError === 'unconfirmed' && (
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-400 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                <div className="text-xs font-semibold text-blue-400">Upload processing — please wait.</div>
              </div>
            </div>
          )}

          {videoWidget.asset_id && !videoWidget.src && !resolveError && (
            <div className="flex items-center gap-2 text-white/50 text-xs">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              Loading video...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
